/**
 * tCms — i18n key lookup with CMS override layer.
 *
 * Usage in a component:
 *   const locale = i18n.language as Locale
 *   <h2>{tCms('home.news.title', locale)}</h2>
 *
 * Lookup chain:
 *   1. cmsOverrides[locale][key]   ← CMS override (from generated TS file)
 *   2. i18next.t(key, { lng: locale })   ← existing JSON fallback
 *   3. '' (empty string)
 *
 * The override map is ALWAYS the nested per-locale shape
 * (`{ "page::slot": { "zh-Hant": …, "zh-Hans": …, en: … } }`) — see
 * `src/cms/overrideMap.ts` for why the flat public-API payload must be
 * normalized before it gets here.
 */

import i18n from '@/i18n'
import type { Locale } from '@/admin/api'
import { cmsOverrides } from '@/content/cms/overrides.generated'
import {
  mergeOverrideDelta,
  readOverride,
  hasOverrideValue,
  type OverrideMap,
} from './overrideMap'

export type { OverrideMap } from './overrideMap'

/** The build-time snapshot, already in the nested per-locale shape. */
const SNAPSHOT = cmsOverrides as unknown as OverrideMap

/**
 * Resolve a CMS-overridable i18n key.
 * @param pageSlug e.g. 'home'
 * @param slotKey  e.g. 'home.news.title'
 * @param locale   e.g. 'zh-Hant'
 * @param overrides  optional runtime overrides (from useCmsOverrides). When
 *                   omitted, the build-time `cmsOverrides` snapshot is used.
 */
export function tCms(
  pageSlug: string,
  slotKey: string,
  locale: Locale,
  overrides?: OverrideMap,
): string {
  const cmsOverrideMap = overrides ?? SNAPSHOT
  const fromCms = readOverride(cmsOverrideMap, pageSlug, slotKey, locale)
  if (typeof fromCms === 'string' && fromCms.length > 0) return fromCms
  return i18n.t(slotKey, { lng: locale }) as string
}

/**
 * Resolve a structured (object / array) i18n key with the CMS delta merged
 * over the i18n default.
 *
 * The default value defines the SHAPE; the CMS override only supplies
 * values. Missing keys inherit the default and arrays keep their default
 * length, so the rendered layout is identical with or without overrides.
 */
export function tCmsObject(
  pageSlug: string,
  slotKey: string,
  locale: Locale,
  overrides?: OverrideMap,
): unknown {
  const cmsOverrideMap = overrides ?? SNAPSHOT
  const fallback = i18n.t(slotKey, { lng: locale, returnObjects: true }) as unknown
  const delta = readOverride(cmsOverrideMap, pageSlug, slotKey, locale)
  if (delta === undefined || delta === null) return fallback
  return mergeOverrideDelta(fallback, delta)
}

/** Has a CMS override been set for this key + locale? */
export function hasOverride(
  pageSlug: string,
  slotKey: string,
  locale: Locale,
  overrides?: OverrideMap,
): boolean {
  return hasOverrideValue(overrides ?? SNAPSHOT, pageSlug, slotKey, locale)
}
